'use client';

import { motion } from 'framer-motion';
import { X, ArrowSquareOut, GithubLogo, Code, Database, DeviceMobile, Globe } from '@phosphor-icons/react';
import Image from 'next/image';
import { Project } from '@/types/project';

interface ProjectModalProps {
  project: Project;
  onClose: () => void;
}

// Mapeamento de tecnologias para ícones do Simple Icons (simulado com Phosphor)
const getTechIcon = (tech: string) => {
  const techLower = tech.toLowerCase();
  if (techLower.includes('react') || techLower.includes('next') || techLower.includes('vue')) return <Code size={16} weight="light" />;
  if (techLower.includes('database') || techLower.includes('sql') || techLower.includes('mongo') || techLower.includes('redis')) return <Database size={16} weight="light" />;
  if (techLower.includes('mobile') || techLower.includes('native') || techLower.includes('expo')) return <DeviceMobile size={16} weight="light" />;
  return <Globe size={16} weight="light" />;
};

export default function ProjectModal({ project, onClose }: ProjectModalProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 sm:p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0, y: 50 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.8, opacity: 0, y: 50 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="bg-black/90 backdrop-blur-md border border-white/10 rounded-2xl sm:rounded-3xl max-w-6xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative border-b border-white/10">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.02] to-transparent" />
          <div className="relative p-4 sm:p-6 lg:p-8">
            <div className="flex items-start justify-between">
              <div className="flex-1 pr-4">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4 tracking-wide leading-tight">{project.name}</h2>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <span
                    className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium border"
                    style={{ 
                      borderColor: `${project.color}40`,
                      backgroundColor: `${project.color}10`,
                      color: project.color
                    }}
                  >
                    {project.mainTech}
                  </span>
                  <span className="text-gray-400 text-xs sm:text-sm font-light">
                    Stack principal
                  </span>
                </div>
              </div>
              <motion.button
                onClick={onClose}
                className="p-2 sm:p-3 hover:bg-white/5 rounded-full transition-colors border border-white/10 hover:border-white/20 min-h-[44px] min-w-[44px] touch-manipulation flex-shrink-0"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <X size={20} weight="light" className="text-gray-400 sm:w-6 sm:h-6" />
              </motion.button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 lg:p-8 overflow-y-auto max-h-[calc(95vh-140px)] sm:max-h-[calc(90vh-200px)]">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12">
            {/* Imagem e Actions */}
            <div className="space-y-4 sm:space-y-6">
              <div className="relative aspect-video rounded-xl sm:rounded-2xl overflow-hidden bg-gray-900/50 border border-white/10">
                <Image
                  src={project.image}
                  alt={project.name}
                  fill
                  className="object-cover filter grayscale"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
              </div>
              
              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <motion.a
                  href={project.demoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex-1 bg-white text-black px-4 sm:px-6 py-3 sm:py-4 rounded-xl font-medium flex items-center justify-center space-x-2 sm:space-x-3 hover:bg-gray-100 transition-all min-h-[48px] touch-manipulation"
                >
                  <ArrowSquareOut size={18} weight="light" className="sm:w-5 sm:h-5" />
                  <span className="text-sm sm:text-base">Ver Demo</span>
                </motion.a>
                <motion.a
                  href={project.repoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex-1 bg-white/5 text-white px-4 sm:px-6 py-3 sm:py-4 rounded-xl font-medium flex items-center justify-center space-x-2 sm:space-x-3 hover:bg-white/10 transition-all border border-white/10 min-h-[48px] touch-manipulation"
                >
                  <GithubLogo size={18} weight="light" className="sm:w-5 sm:h-5" />
                  <span className="text-sm sm:text-base">Código</span>
                </motion.a>
              </div>
            </div>

            {/* Detalhes */}
            <div className="space-y-6 sm:space-y-8">
              {/* Descrição */}
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4 tracking-wide">Sobre o Projeto</h3>
                <p className="text-gray-300 leading-relaxed font-light text-sm sm:text-base lg:text-lg">{project.description}</p>
              </div>

              {/* Tecnologias */}
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 tracking-wide">Stack Tecnológica</h3>
                <div className="space-y-3 sm:space-y-4">
                  {/* Tecnologia Principal */}
                  <div className="p-4 sm:p-5 lg:p-6 bg-white/[0.02] rounded-xl sm:rounded-2xl border border-white/10">
                    <div className="flex items-center space-x-3 sm:space-x-4">
                      <div
                        className="w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2"
                        style={{ borderColor: project.color, backgroundColor: `${project.color}20` }}
                      />
                      <span className="text-white font-medium text-base sm:text-lg">{project.mainTech}</span>
                      <span className="text-xs bg-white/10 text-gray-300 px-2 sm:px-3 py-1 rounded-full font-medium">
                        Principal
                      </span>
                    </div>
                  </div>

                  {/* Outras Tecnologias */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                    {project.technologies.slice(1).map((tech, index) => (
                      <motion.div
                        key={tech}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-center space-x-2 sm:space-x-3 p-3 sm:p-4 bg-white/[0.01] rounded-lg sm:rounded-xl border border-white/5 hover:bg-white/[0.02] hover:border-white/10 transition-all"
                      >
                        {getTechIcon(tech)}
                        <span className="text-gray-300 text-xs sm:text-sm font-medium">{tech}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Características */}
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 tracking-wide">Características</h3>
                <div className="space-y-2 sm:space-y-3">
                  {[
                    "Design responsivo e moderno",
                    "Performance otimizada",
                    "Código limpo e documentado",
                    "Testes automatizados",
                    "Deploy automatizado",
                    "Arquitetura escalável"
                  ].map((feature, index) => (
                    <motion.div
                      key={feature}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center space-x-2 sm:space-x-3 text-gray-300"
                    >
                      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full opacity-60 flex-shrink-0" />
                      <span className="text-xs sm:text-sm font-medium">{feature}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}